import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';

// Core
import { ErrorMessage } from '../../../core/model/error-message';
import { IBoolResult } from '../../../core/interfaces/bool-result';
import { UiMessageSnackService } from '../../../core/components/message-snack/service/message-snack.service';

// Redux
import { NgRedux, select } from '@angular-redux/store';
import { IAppState } from '../../../core/store/store';

// foreign feature
import {
    breadcrumbAddCrumb, breadcrumbSetInitialCrumb, breadcrumbAddCrumbAndShrink
} from '../../../core/components/breadcrumb/store/actions';
// foreign feature
import { NavElementsContainer } from '../../model/nav-elements-container';

// Own feature
import { NavigationService } from '../../../nav/service/navigation.service';
import {
    navigationStoreResult1stDetail,
    navigationResetStateAllExceptConfig,
    navigationStore1stRequestOptions,
    navigationStore2stRequestOptions,
    navigationStore1stDisplayOptionEdges,
    navigationStore2stDisplayOptionEdges
} from '../../store/actions';
import { INavRequestOption, INavRequestOptionEdges } from '../../model/nav-request-option.interface';
import { INavGraphDisplayOptionEdges } from '../../model/nav-display-edgegroup-pairs';
import { NavElementsRoot } from '../../model/nav-elements-root';
import { navigationConfig } from '../../config/navigation.config';
import { menuShowNavSearch } from '../../../menu/store/actions';
import { ReloadContent, UpdatableContent } from '../../model/reload-content.abstract';
import { INavConfig } from '../../store/store';
import { LoadingphaseService } from '../../service/loadingphase.service';
import { ConfirmDialogsService } from '../../../core/components/confirm-dialog/service/confirm-dialog.service';

// Sub View
const enum SubView {
    HIDE = 0,
    GRAPH_VIEW = 1 << 1,  // 2
    LIST_VIEW = 2 << 1,  // 4
    // exceptions
    INTERNAL_ERROR = 1 << 11, // 2048
}

// export
export let gNavElementContainer: NavElementsContainer = null;
export let gNavContainer: NavContComponent = null;
export let gVirtualRootId: string[] = [];       // enthält max 1 item

@Component({
    selector: 'app-navigation-cont',
    templateUrl: './nav-cont.component.html',
    styleUrls: ['./nav-cont.component.scss']
})
export class NavContComponent implements OnInit, OnDestroy {
    private readonly = true;
    // container
    private _navElementContainer: NavElementsContainer = null;

    // breadcrumb: in graph -> true, in list -> false
    public disableLinkBreadCrumb = false;

    // loading data
    public set _loadingPhase(value: boolean) {
        this._loadingphaseService.setLoadingphase(value, 'nav_cont');
    }
    // pages
    private _subView = SubView.GRAPH_VIEW;     // initial SubView

    // Observables from store
    @select((s: IAppState) => s.navigationState.elementsResult1stInitial) _navElementResultInit$;
    @select((s: IAppState) => s.navigationState.element1stDisplayOptions) _navElementOptions$;
    @select((s: IAppState) => s.navigationState.element1stDetail) navElementDetail$;
    @select((s: IAppState) => s.breadcrumbState.crumbSelected0) breadcrumbSelected$;

    // children
    @ViewChild('NavGraph1') public readonly navGraph1ReloadContent: ReloadContent = null;
    @ViewChild('NavList') public readonly navListReloadContent: UpdatableContent = null;

    constructor(
        private _router: Router,
        private _store: NgRedux<IAppState>,
        private _navigationService: NavigationService,
        private _messageSnackService: UiMessageSnackService,
        private _loadingphaseService: LoadingphaseService,
        private _confirmDialogService: ConfirmDialogsService,
    ) {
        // export
        gNavElementContainer = this._navElementContainer = new NavElementsContainer(this._messageSnackService);
        gNavContainer = this;
    }

    // ----------------------------------------------------------------------------------------------
    // -- onInit/onDestroy
    // ----------------------------------------------------------------------------------------------
    ngOnInit() {

        // update request options with definition in config.search
        const requestOptions: INavRequestOption = this._store.getState().navigationState.element1stRequestOptions;
        const navConfig: INavConfig = this._store.getState().navigationState.config;

        // sanity
        if (!navConfig.hasOwnProperty('search') || !navConfig.search.hasOwnProperty('edges')) {
            console.warn('Search oder Search.Edges Key in Config nicht vorhanden');
            return;
        }
        if (!navConfig.hasOwnProperty('dictionary')) {
            console.warn('Dictionary Key in Config nicht vorhanden');
            return;
        }

        // edge in searchOption + displayOption
        const searchOptionEdges: INavRequestOptionEdges[] = [];
        const displayOptionEdges: INavGraphDisplayOptionEdges[] = [];

        // loop config -> search -> edges
        navConfig.search.edges.forEach(confElem => {
            // loop config -> dictionary
            navConfig.dictionary.forEach(dictElem => {

                if (dictElem['key'] === confElem) {
                    // search option
                    searchOptionEdges.push({
                        uiText: dictElem.fwd,
                        value: dictElem.key,
                        search: true
                    });
                    // display option
                    displayOptionEdges.push({
                        uiText: dictElem.fwd,
                        value: dictElem.key,
                        existInCont: false
                    });
                }

            });
        });

        // merge and save search options
        this._store.dispatch(navigationStore1stRequestOptions(
            {
                input: requestOptions.input,
                edges: searchOptionEdges,
                maxnodes: requestOptions.maxnodes,
                loadOptions: requestOptions.loadOptions
            }
        ));
        this._store.dispatch(navigationStore2stRequestOptions(
            {
                input: requestOptions.input,
                edges: searchOptionEdges,
                maxnodes: requestOptions.maxnodes,
                loadOptions: requestOptions.loadOptions
            }
        ));
        // save display options
        this._store.dispatch(navigationStore1stDisplayOptionEdges(displayOptionEdges));
        this._store.dispatch(navigationStore2stDisplayOptionEdges(displayOptionEdges));

        // set flag in store to show nav search in menu
        this._store.dispatch(menuShowNavSearch(true));
    }

    ngOnDestroy() {
        // nav route leave
        this._store.dispatch(menuShowNavSearch(false));

        // sanity
        if (!this._store) { return; }

        // reset navigation data in store
        this._store.dispatch(navigationResetStateAllExceptConfig());
    }

    // ----------------------------------------------------------------------------------------------
    // -- Localization
    // ----------------------------------------------------------------------------------------------


    // ----------------------------------------------------------------------------------------------
    // -- Visibility
    // ----------------------------------------------------------------------------------------------
    public showView(): boolean { return this._subView !== SubView.HIDE; }
    public showSubView1(): boolean { return this._subView === SubView.GRAPH_VIEW; }
    public showSubView2(): boolean { return this._subView === SubView.LIST_VIEW; }
    // exceptions
    public showSubViewIntErr(): boolean { return this._subView === SubView.INTERNAL_ERROR; }

    // ----------------------------------------------------------------------------------------------
    // -- TabHeader
    // ----------------------------------------------------------------------------------------------
    public onSelectViewGraphTrigger() {

        // sanity
        if (!gNavElementContainer.getVirtualRoot() ||
            gNavElementContainer.getVirtualRootId() === navigationConfig.defaultValuesForRoot.id) {

            // switch to graph view
            this._subView = SubView.GRAPH_VIEW;
            return;
        }

        this._loadingPhase = true;

        // -->>ASY
        this._navigationService.loadElements1stDetail(
            gNavElementContainer.getVirtualRootId()
        )
            .then((boolResultASY: IBoolResult) => {
                this._loadingPhase = false;

                // result available
                if (boolResultASY.result) {
                    // enable breadcrumb
                    this.disableLinkBreadCrumb = false;

                    this._subView = SubView.GRAPH_VIEW;
                    return;
                }

                // no data
                this._messageSnackService.showSnackWarning(boolResultASY.message.message, 'NavElemGraphViewComponent');
            })
            .catch((err: ErrorMessage) => {
                this._loadingPhase = false;
                this._messageSnackService.showSnackError(err.message, 'NavElemGraphViewComponent');
            });
    }

    public onSelectViewListTrigger() {

        // sende request und expandiere selektierten Knoten mit Edge Tiefe 1
        if (!gNavElementContainer) {
            console.warn('Miss Element Container');
            return;
        }

        // virtual root
        const selectedNode: NavElementsRoot = _.cloneDeep(gNavElementContainer.getVirtualRoot());

        // sanity - wenn 'Result' selekiert
        if (selectedNode.rootObj.id === navigationConfig.defaultValuesForRoot.id) {
            this._messageSnackService.showSnackWarning('Für das Suchergebnis ist keine Listenansicht möglich');
            return;
        }

        // prepare search request
        const requestOptions: INavRequestOption = this._store.getState().navigationState.element1stRequestOptions;
        // sanity
        if (!requestOptions) {
            console.warn('Miss Search Options in Store');
            return;
        }
        // enable search with all edges
        requestOptions.edges.forEach((ei: INavRequestOptionEdges) => {
            ei.search = true;
        });
        requestOptions.maxnodes = navigationConfig.loadRequestMaxElements;
        requestOptions.loadOptions = 'list';

        // set object rootToExtend in navElementContainer - to verify 'root' of result from backend
        gNavElementContainer.setRootToExtend(selectedNode.rootObj.id);


        // disable breadcrumb
        this.disableLinkBreadCrumb = true;

        // ok - zum list view
        this._subView = SubView.LIST_VIEW;
        this.navListReloadContent.reloadContent();
    }

    // ----------------------------------------------------------------------------------------------
    // -- Event Handler
    // ----------------------------------------------------------------------------------------------
    public onCancelDetailView() {
        // reset detail data in store -> hide detail dialog
        this._store.dispatch(navigationStoreResult1stDetail(null));
    }

    public onSubmitDetailView(elementDetail: any, confirmed?: boolean) {
        this._loadingPhase = true;

        // -->>ASY
        this._navigationService.saveElements1stDetail(
            elementDetail,
            confirmed,
        )
            .then((boolResultASY: IBoolResult) => {
                this._loadingPhase = false;

                // result available
                if (boolResultASY.result) {

                    // finde node in gNavElementContainer
                    gNavElementContainer.updateNode(elementDetail.data, this._store.getState().navigationState.config);

                    if(this._subView == SubView.LIST_VIEW) {
                        this.navListReloadContent.updateNode(elementDetail.data._id, elementDetail.data.bezeichnung);
                    }

                    this._messageSnackService.logMessage('SUCCESS', {
                        uiMessage: 'Element-Details wurden gespeichert',
                    });
                    return;
                }

                // no data
                this._messageSnackService.logMessage('ERROR', {
                    uiMessage: boolResultASY.message.message,
                });
            })
            .catch((err: ErrorMessage) => {
                this._loadingPhase = false;
                if (err.code === 'missingConfirm') {
                    this._confirmDialogService.openHelpMessage('Manuell ausgelöste Funktionsgruppe', err.uiMessage, true).subscribe(
                        (res) => {
                            this.onSubmitDetailView(elementDetail, res)
                        }
                    );
                } else {
                    this._messageSnackService.logMessage('ERROR', {
                        uiMessage: err.message
                    });
                }
            });
    }

    /**
    * Container zeigt die Graph View an, z.B. wenn während der anzeige des Listen Views
    * eine neue Initiale Suche ausgeführt wird.
    *
    * @returns void
    */
    public showGraphView(): void {
        // enable breadcrumb
        this.disableLinkBreadCrumb = false;

        // show graph
        this._subView = SubView.GRAPH_VIEW;
    }

    public setReadonly(hasRole: boolean) {
        this.readonly = !hasRole;
    }
}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/nav/compc/nav-cont/nav-cont.component.ts